import { slugifyArticleTitle } from './growth-article-publishing.util';
import { combineUtcDateAndTime, splitUtcDateTime } from './growth-datetime.util';
import { GrowthContentAsset, UpsertGrowthContentAssetPayload } from './growth-marketing.service';

export type GrowthContentBodyFormat = 'markdown' | 'html';

export interface GrowthContentAssetEditorModel {
  title: string;
  slug: string;
  contentType: string;
  ownerScope: string;
  locale: string;
  summary: string;
  bodyMarkdown: string;
  bodyFormat: GrowthContentBodyFormat;
}

export interface GrowthContentCitationRow {
  sourceType: string;
  sourceRef: string;
  note: string;
}

export interface GrowthContentPublishTargetRow {
  channelKey: string;
  targetRef: string | null;
  scheduleDateUtc: string | null;
  scheduleTimeUtc: string;
}

export interface GrowthContentAssetSeoModel {
  metaTitle: string;
  metaDescription: string;
  keywords: string;
  titleFa: string;
  titleEn: string;
  descriptionFa: string;
  descriptionEn: string;
  ogImage: string;
}

export interface GrowthContentAssetEditorState {
  model: GrowthContentAssetEditorModel;
  seo: GrowthContentAssetSeoModel;
  existingBodyJson: string | null;
  citationRows: GrowthContentCitationRow[];
  publishTargetRows: GrowthContentPublishTargetRow[];
  slugManuallyEdited: boolean;
}

const CITATION_SELECTOR_TYPES = new Set([
  'catalog',
  'offering',
  'segment',
  'content-asset',
  'knowledge',
  'seo-profile'
]);

export function createDefaultGrowthContentAssetSeoModel(): GrowthContentAssetSeoModel {
  return {
    metaTitle: '',
    metaDescription: '',
    keywords: '',
    titleFa: '',
    titleEn: '',
    descriptionFa: '',
    descriptionEn: '',
    ogImage: ''
  };
}

export function createDefaultGrowthContentAssetEditorState(): GrowthContentAssetEditorState {
  return {
    model: {
      title: '',
      slug: '',
      contentType: 'article',
      ownerScope: 'tenant',
      locale: 'fa-IR',
      summary: '',
      bodyMarkdown: '',
      bodyFormat: 'markdown'
    },
    seo: createDefaultGrowthContentAssetSeoModel(),
    existingBodyJson: null,
    citationRows: [{ sourceType: 'knowledge', sourceRef: '', note: '' }],
    publishTargetRows: [{ channelKey: 'web-blog', targetRef: '', scheduleDateUtc: null, scheduleTimeUtc: '09:00' }],
    slugManuallyEdited: false
  };
}

export function parseGrowthContentAssetSeoFromBodyJson(bodyJson: string | null | undefined): GrowthContentAssetSeoModel {
  const defaults = createDefaultGrowthContentAssetSeoModel();
  if (!bodyJson?.trim()) {
    return defaults;
  }

  try {
    const root = JSON.parse(bodyJson) as Record<string, unknown>;
    const seoRaw = root['seo'];
    if (!seoRaw || typeof seoRaw !== 'object') {
      return defaults;
    }
    const seo = seoRaw as Record<string, unknown>;
    return {
      metaTitle: readSeoString(seo, 'metaTitle', 'title'),
      metaDescription: readSeoString(seo, 'metaDescription', 'description'),
      keywords: readSeoKeywords(seo),
      titleFa: readSeoString(seo, 'titleFa'),
      titleEn: readSeoString(seo, 'titleEn'),
      descriptionFa: readSeoString(seo, 'descriptionFa'),
      descriptionEn: readSeoString(seo, 'descriptionEn'),
      ogImage: readSeoString(seo, 'ogImage')
    };
  } catch {
    return defaults;
  }
}

function readSeoString(seo: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = seo[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

function readSeoKeywords(seo: Record<string, unknown>): string {
  const value = seo['keywords'];
  if (Array.isArray(value)) {
    return value
      .filter(item => typeof item === 'string' && item.trim())
      .map(item => (item as string).trim())
      .join(', ');
  }
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  return '';
}

function parseKeywordsForBodyJson(keywords: string): string[] {
  return keywords
    .split(/[,،]/)
    .map(item => item.trim())
    .filter(item => item.length > 0);
}

function growthContentAssetSeoHasContent(seo: GrowthContentAssetSeoModel): boolean {
  return Boolean(
    seo.metaTitle.trim() ||
      seo.metaDescription.trim() ||
      seo.keywords.trim() ||
      seo.titleFa.trim() ||
      seo.titleEn.trim() ||
      seo.descriptionFa.trim() ||
      seo.descriptionEn.trim() ||
      seo.ogImage.trim()
  );
}

function buildSeoObjectForBodyJson(seo: GrowthContentAssetSeoModel, locale: string): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  const metaTitle = seo.metaTitle.trim();
  const metaDescription = seo.metaDescription.trim();
  if (metaTitle) {
    result['metaTitle'] = metaTitle;
    result['title'] = metaTitle;
  }
  if (metaDescription) {
    result['metaDescription'] = metaDescription;
    result['description'] = metaDescription;
  }
  const keywords = parseKeywordsForBodyJson(seo.keywords);
  if (keywords.length) {
    result['keywords'] = keywords;
  }
  if (seo.titleFa.trim()) {
    result['titleFa'] = seo.titleFa.trim();
  }
  if (seo.titleEn.trim()) {
    result['titleEn'] = seo.titleEn.trim();
  }
  if (seo.descriptionFa.trim()) {
    result['descriptionFa'] = seo.descriptionFa.trim();
  }
  if (seo.descriptionEn.trim()) {
    result['descriptionEn'] = seo.descriptionEn.trim();
  }
  if (seo.ogImage.trim()) {
    result['ogImage'] = seo.ogImage.trim();
  }
  const localeValue = locale.trim();
  if (localeValue) {
    result['locale'] = localeValue;
  }
  result['generatedBy'] = 'admin-editor';
  return result;
}

export function buildGrowthContentAssetBodyJson(state: GrowthContentAssetEditorState): string | null {
  let root: Record<string, unknown> = {};
  if (state.existingBodyJson?.trim()) {
    try {
      const parsed = JSON.parse(state.existingBodyJson) as Record<string, unknown>;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        root = { ...parsed };
      }
    } catch {
      root = {};
    }
  }

  if (growthContentAssetSeoHasContent(state.seo)) {
    root['seo'] = buildSeoObjectForBodyJson(state.seo, state.model.locale);
  } else {
    delete root['seo'];
  }

  const keys = Object.keys(root);
  if (!keys.length) {
    return null;
  }
  return JSON.stringify(root);
}

export function normalizeGrowthContentBodyFormat(value: string | null | undefined): GrowthContentBodyFormat {
  return value?.trim().toLowerCase() === 'html' ? 'html' : 'markdown';
}

export function growthContentAssetEditorStateFromAsset(asset: GrowthContentAsset): GrowthContentAssetEditorState {
  const bodyFormat = normalizeGrowthContentBodyFormat(asset.bodyFormat);
  const existingBodyJson = asset.bodyJson ?? null;
  return {
    model: {
      title: asset.title,
      slug: asset.slug ?? slugifyArticleTitle(asset.title),
      contentType: asset.contentType,
      ownerScope: asset.ownerScope,
      locale: asset.locale,
      summary: asset.summary ?? '',
      bodyMarkdown: asset.bodyMarkdown ?? '',
      bodyFormat
    },
    seo: parseGrowthContentAssetSeoFromBodyJson(existingBodyJson),
    existingBodyJson,
    slugManuallyEdited: Boolean(asset.slug),
    citationRows: parseGrowthContentCitations(asset.citationRefsJson),
    publishTargetRows: parseGrowthContentPublishTargets(asset.publishTargetsJson)
  };
}

export function patchGrowthContentAssetEditorFromWizardContext(
  state: GrowthContentAssetEditorState,
  context: Record<string, string>
): GrowthContentAssetEditorState {
  const next = cloneGrowthContentAssetEditorState(state);

  if (context['title']) {
    next.model.title = context['title'];
  }
  if (context['publicSlug']) {
    next.model.slug = context['publicSlug'];
    next.slugManuallyEdited = true;
  }
  if (context['locale']) {
    next.model.locale = context['locale'];
  }
  if (context['summary']) {
    next.model.summary = context['summary'];
  }
  if (context['bodyMarkdown']) {
    next.model.bodyMarkdown = context['bodyMarkdown'];
  }
  if (context['contentType']) {
    next.model.contentType = context['contentType'];
  }
  if (context['ownerScope']) {
    next.model.ownerScope = context['ownerScope'];
  }
  if (context['bodyFormat']) {
    next.model.bodyFormat = normalizeGrowthContentBodyFormat(context['bodyFormat']);
  }
  if (context['citationRefsJson']) {
    next.citationRows = parseGrowthContentCitations(context['citationRefsJson']);
  }
  if (context['publishTargetsJson']) {
    next.publishTargetRows = parseGrowthContentPublishTargets(context['publishTargetsJson']);
  }
  if (context['bodyJson']) {
    next.existingBodyJson = context['bodyJson'];
    next.seo = parseGrowthContentAssetSeoFromBodyJson(context['bodyJson']);
  }

  return next;
}

export function cloneGrowthContentAssetEditorState(state: GrowthContentAssetEditorState): GrowthContentAssetEditorState {
  return {
    model: { ...state.model },
    seo: { ...state.seo },
    existingBodyJson: state.existingBodyJson,
    citationRows: state.citationRows.map(row => ({ ...row })),
    publishTargetRows: state.publishTargetRows.map(row => ({ ...row })),
    slugManuallyEdited: state.slugManuallyEdited
  };
}

export function buildGrowthArticlePublishContentStepPayload(state: GrowthContentAssetEditorState): Record<string, unknown> {
  const model = state.model;
  const slug = model.slug.trim() || slugifyArticleTitle(model.title);
  return {
    title: model.title.trim(),
    slug: slug || null,
    locale: model.locale.trim() || 'fa-IR',
    summary: model.summary.trim() || null,
    bodyMarkdown: model.bodyMarkdown.trim() || null,
    bodyFormat: model.bodyFormat,
    contentType: model.contentType.trim() || 'article',
    ownerScope: model.ownerScope.trim() || 'tenant',
    citationRefsJson: JSON.stringify(state.citationRows),
    publishTargetsJson: JSON.stringify(toGrowthContentPublishTargetApiRows(state.publishTargetRows)),
    bodyJson: buildGrowthContentAssetBodyJson(state)
  };
}

export function buildUpsertGrowthContentAssetPayloadFromEditor(
  state: GrowthContentAssetEditorState,
  options: { id?: string | null; aiAssistDraftId?: string | null } = {}
): UpsertGrowthContentAssetPayload {
  const model = state.model;
  return {
    id: options.id ?? null,
    title: model.title.trim(),
    slug: model.slug.trim() || slugifyArticleTitle(model.title),
    contentType: model.contentType,
    ownerScope: model.ownerScope,
    locale: model.locale.trim(),
    summary: model.summary,
    bodyMarkdown: model.bodyMarkdown,
    bodyFormat: model.bodyFormat,
    citationRefsJson: JSON.stringify(state.citationRows),
    publishTargetsJson: JSON.stringify(toGrowthContentPublishTargetApiRows(state.publishTargetRows)),
    bodyJson: buildGrowthContentAssetBodyJson(state),
    aiGenerated: false,
    aiAssistDraftId: options.aiAssistDraftId ?? null
  };
}

export function onGrowthContentAssetTitleChanged(state: GrowthContentAssetEditorState): GrowthContentAssetEditorState {
  if (state.slugManuallyEdited) {
    return state;
  }
  const next = cloneGrowthContentAssetEditorState(state);
  next.model.slug = slugifyArticleTitle(next.model.title);
  return next;
}

export function parseGrowthContentCitations(json: string): GrowthContentCitationRow[] {
  try {
    const rows = JSON.parse(json) as GrowthContentCitationRow[];
    return rows.map(row => ({
      sourceType: normalizeGrowthCitationSourceType(row.sourceType),
      sourceRef: row.sourceRef ?? '',
      note: row.note ?? ''
    }));
  } catch {
    return [{ sourceType: 'knowledge', sourceRef: '', note: '' }];
  }
}

function normalizeGrowthCitationSourceType(sourceType: string | undefined): string {
  const normalized = (sourceType ?? '').trim().toLowerCase();
  if (!normalized) {
    return 'knowledge';
  }
  if (CITATION_SELECTOR_TYPES.has(normalized) || normalized === 'manual') {
    return normalized;
  }
  return 'manual';
}

export function parseGrowthContentPublishTargets(json: string): GrowthContentPublishTargetRow[] {
  try {
    const parsed = JSON.parse(json) as Array<{ channelKey: string; targetRef: string | null; scheduledAtUtc: string | null }>;
    return parsed.map(row => {
      const split = splitUtcDateTime(row.scheduledAtUtc);
      return {
        channelKey: row.channelKey,
        targetRef: row.targetRef,
        scheduleDateUtc: split.dateIso,
        scheduleTimeUtc: split.time
      };
    });
  } catch {
    return [{ channelKey: 'web-blog', targetRef: '', scheduleDateUtc: null, scheduleTimeUtc: '09:00' }];
  }
}

export function toGrowthContentPublishTargetApiRows(
  rows: GrowthContentPublishTargetRow[]
): Array<{ channelKey: string; targetRef: string | null; scheduledAtUtc: string | null }> {
  return rows.map(row => ({
    channelKey: row.channelKey,
    targetRef: row.targetRef,
    scheduledAtUtc: combineUtcDateAndTime(row.scheduleDateUtc, row.scheduleTimeUtc)
  }));
}
